<template>
    <LoadingCard v-if="loading" />
    <div class="content__inner" v-else>
        <header class="content__title pl-0">
            <h1>{{ this.id ? 'Update purchase' : 'Add a new purchase' }}</h1>

        </header>
        <form @submit.prevent="attemptCreate" ref="createForm" novalidate>
            <div class="card">
                <div class="card-header bg-primary text-white">
                    Required Details
                </div>

                <div class="card-body pt-3 pb-1">

                    <!-- Purchase Date -->
                    <div class="form-group row">
                        <label for="purchase_on" class="col-sm-2 col-form-label">Purchase Date</label>
                        <div class="input-group col-sm-10">
                            <VueDatePicker
                                    id="purchase_on"
                                    v-model="purchased_on"
                                    :config="datePicker"
                                    class="form-control"
                                    :class="{'is-invalid': errors.has('purchased_on')}"
                                    placeholder="Select date"
                                    v-validate="validation.purchased_on"
                                    name="purchased_on" />
                            <i class="form-group__bar"></i>
                            <div class="invalid-feedback" v-if="errors.has('purchased_on')">{{ errors.first('purchased_on') }}</div>
                        </div>
                    </div>
                </div>

                <!-- Chemicals Used -->
                <div class="bg-light">
                    <h6 class="card-header text-secondary">Chemicals Purchased</h6>
                </div>

                <div class="card-body pt-1">

                    <b-table hover small stacked="md" :items="entryRows" :fields="entriesTable.fields" :show-empty="true">
                        <template slot="chemical" slot-scope="row">
                            <div class="position-relative">
                                <VueSelect
                                        v-b-tooltip="errors.first(`chemical_${row.index}`) || ''"
                                        @input="setRow('chemical', row.index, $event)"
                                        :value="row.item.chemical"
                                        v-validate="validation.entry_chemical"
                                        :data-vv-name="`chemical_${row.index}`"
                                        data-vv-as="chemical"
                                        :options="linkedChemicals"
                                        :allow-empty="false"
                                        :show-labels="false"
                                        placeholder="Please Select..."
                                        label="name"
                                        track-by="id"
                                >
                                </VueSelect>
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="container_price" slot-scope="row">
                            <div class="position-relative">
                                <VueMask :value="row.item.container_price" class="form-control"
                                         v-b-tooltip="errors.first(`price_${row.index}`) || ''"
                                         :class="{'is-invalid': errors.has(`price_${row.index}`)}"
                                         placeholder="0..."
                                         v-validate="validation.entry_container_price"
                                         :data-vv-name="`price_${row.index}`"
                                         data-vv-as="container price"
                                         :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}"
                                         @input="setRow('container_price', row.index, $event)" required />
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="container_volume" slot-scope="row">
                            <div class="position-relative">
                                <VueMask :value="row.item.container_volume" class="form-control"
                                         v-b-tooltip="errors.first(`volume_${row.index}`) || ''"
                                         :class="{'is-invalid': errors.has(`volume_${row.index}`)}"
                                         placeholder="0..."
                                         v-validate="validation.entry_container_volume"
                                         :data-vv-name="`volume_${row.index}`"
                                         data-vv-as="container volume"
                                         :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}"
                                         @input="setRow('container_volume', row.index, $event)" required />
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="container_count" slot-scope="row">
                            <div class="position-relative">
                                <VueMask :value="row.item.container_count" class="form-control"
                                         v-b-tooltip="errors.first(`count_${row.index}`) || ''"
                                         :class="{'is-invalid': errors.has(`count_${row.index}`)}"
                                         placeholder="0..."
                                         v-validate="validation.entry_container_count"
                                         :data-vv-name="`count_${row.index}`"
                                         data-vv-as="container count"
                                         :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}"
                                         @input="setRow('container_count', row.index, $event)" required />
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="remove" slot-scope="row">
                            <b-button size="sm" variant="outline-danger" @click="removeRow(row.index)">Remove</b-button>
                        </template>
                    </b-table>

                    <div class="text-right">
                        <button type="button" class="btn btn-success" @click.prevent="addRow">Add Another Chemical</button>
                    </div>

                </div>

                <!-- Fertilisers Used -->
                <div class="bg-light">
                    <h6 class="card-header text-secondary">Fertilisers Purchased</h6>
                </div>

                <div class="card-body pt-1">
                    <b-table hover small stacked="md" :items="fertiliserRows" :fields="fertiliserEntriesTable.fields" :show-empty="true">
                        <template slot="fertiliser" slot-scope="row">
                            <div class="position-relative">
                                <VueSelect
                                        v-b-tooltip="errors.first(`fertiliser_${row.index}`) || ''"
                                        @input="setFertiliserRow('fertiliser', row.index, $event)"
                                        :value="row.item.fertiliser"
                                        v-validate="validation.entry_fertiliser"
                                        :data-vv-name="`fertiliser_${row.index}`"
                                        data-vv-as="fertiliser"
                                        :options="linkedFertilisers"
                                        :allow-empty="false"
                                        :show-labels="false"
                                        placeholder="Please Select..."
                                        label="name"
                                        track-by="id"
                                >
                                </VueSelect>
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="container_price" slot-scope="row">
                            <div class="position-relative">
                                <VueMask :value="row.item.container_price" class="form-control"
                                         v-b-tooltip="errors.first(`fertiliser_price_${row.index}`) || ''"
                                         :class="{'is-invalid': errors.has(`fertiliser_price_${row.index}`)}"
                                         placeholder="0..."
                                         v-validate="validation.entry_container_price"
                                         :data-vv-name="`fertiliser_price_${row.index}`"
                                         data-vv-as="container price"
                                         :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}"
                                         @input="setFertiliserRow('container_price', row.index, $event)" required />
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="container_volume" slot-scope="row">
                            <div class="position-relative">
                                <VueMask :value="row.item.container_volume" class="form-control"
                                         v-b-tooltip="errors.first(`fertiliser_volume_${row.index}`) || ''"
                                         :class="{'is-invalid': errors.has(`fertiliser_volume_${row.index}`)}"
                                         placeholder="0..."
                                         v-validate="validation.entry_container_volume"
                                         :data-vv-name="`fertiliser_volume_${row.index}`"
                                         data-vv-as="container volume"
                                         :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}"
                                         @input="setFertiliserRow('container_volume', row.index, $event)" required />
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="container_count" slot-scope="row">
                            <div class="position-relative">
                                <VueMask :value="row.item.container_count" class="form-control"
                                         v-b-tooltip="errors.first(`fertiliser_count_${row.index}`) || ''"
                                         :class="{'is-invalid': errors.has(`fertiliser_count_${row.index}`)}"
                                         placeholder="0..."
                                         v-validate="validation.entry_container_count"
                                         :data-vv-name="`fertiliser_count_${row.index}`"
                                         data-vv-as="container count"
                                         :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}"
                                         @input="setFertiliserRow('container_count', row.index, $event)" required />
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="remove" slot-scope="row">
                            <b-button size="sm" variant="outline-danger" @click="removeFertiliserRow(row.index)">Remove</b-button>
                        </template>
                    </b-table>

                    <div class="text-right">
                        <button type="button" class="btn btn-success" @click.prevent="addFertiliserRow">Add Another Fertiliser</button>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-header bg-primary text-white">
                    Optional Details
                </div>

                <div class="card-body pt-3">

                    <!-- Comments -->
                    <div class="form-group row">
                        <label class="col-sm-2 col-form-label">Comments</label>
                        <div class="col-sm-10">
                            <div class="position-relative">
                                <textarea class="form-control" v-model="comments" placeholder="Comments..." v-autosize></textarea>
                                <div class="invalid-feedback" v-if="errors.has('comments')">{{ errors.first('comments') }}</div>
                                <i class="form-group__bar"></i>
                            </div>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-sm-6">
                            <button type="button" @click="attemptDelete" v-if="id" class="btn btn-danger">Delete</button>
                        </div>
                        <div class="col-sm-6 text-right">
                            <button type="submit" class="btn btn-success">{{ this.id ? 'Update' : 'Create' }}</button>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import deleteChemicalPurchaseMutation from '@views/StockManagement/PurchaseInputManager/mutationDeleteChemicalPurchase.graphql';
import createPurchaseInputMutation from '@views/StockManagement/PurchaseInputManager/mutationCreatePurchaseInput.graphql';
import updatePurchaseInputMutation from '@views/StockManagement/PurchaseInputManager/mutationUpdatePurchaseInput.graphql';
import listLinkedChemicalsQuery from '@views/StockManagement/PurchaseInputManager/queryListGrowerLinkedChemicalCommercialVariants.graphql';
import listLinkedFertilisersQuery from '@views/StockManagement/PurchaseInputManager/queryListGrowerLinkedFertilisers.graphql';
import findChemicalPurchaseQuery from '@views/StockManagement/PurchaseInputManager/queryFindChemicalPurchase.graphql';

import ConfirmDatePlugin from 'flatpickr/dist/plugins/confirmDate/confirmDate';

const datePickerConfig = (config = {}) => {
  return {
    plugins: [new ConfirmDatePlugin({ confirmIcon: '' })],
    wrap: true, // set wrap to true only when using 'input-group'
    altInput: true,
    nextArrow: '<i class="zmdi zmdi-long-arrow-right" />',
    prevArrow: '<i class="zmdi zmdi-long-arrow-left" />',
    ...config,
  };
};

export default {
  name: 'CreatePurchaseInput',
  apollo: {
    linkedChemicals: {
      query: listLinkedChemicalsQuery,
      update: data => data.filterGrowerChemicalCommercialVariantLinks,
      variables() {
        if (this.userId) {
          return {
            input: {
              user_id: this.userId,
            },
          };
        }
      },
    },
    linkedFertilisers: {
      query: listLinkedFertilisersQuery,
      update: data => data.filterGrowerFertiliserLinks,
      variables() {
        if (this.userId) {
          return {
            input: {
              user_id: this.userId,
            },
          };
        }
      },
    },
    currentEntry: {
      query: findChemicalPurchaseQuery,
      manual: true,
      result({ loading, data }) {
        if (!loading) {
          this.comments = data.findChemicalPurchase.comments;
          this.purchased_on = data.findChemicalPurchase.purchased_on;
          this.entryRows = data.findChemicalPurchase.entries.map(item => ({ ...item }));
          this.fertiliserRows = data.findChemicalPurchase.fertilisers.map(item => ({ ...item }));
        }
      },
      variables() {
        return {
          input: {
            id: this.id,
          },
        };
      },
      skip() {
        return !this.id;
      },
    },
  },
  props: {
    id: {},
  },
  data() {
    return {
      loading: 0,
      comments: null,
      purchased_on: null,
      currentEntry: null,
      entryRows: Array.from({ length: 3 }, () => ({
        chemical: null,
        container_volume: null,
        container_price: null,
        container_count: null,
      })),
      fertiliserRows: Array.from({ length: 3 }, () => ({
        fertiliser: null,
        container_volume: null,
        container_price: null,
        container_count: null,
      })),
      linkedChemicals: [],
      linkedFertilisers: [],
      entriesTable: {
        fields: [
          { key: 'chemical', label: 'Chemical' },
          { key: 'container_price', label: '$/Container' },
          { key: 'container_count', label: 'Num. Containers' },
          { key: 'container_volume', label: 'Container Volume (Kg/Lt)' },
          { key: 'remove', label: 'Remove' },
        ],
      },
      fertiliserEntriesTable: {
        fields: [
          { key: 'fertiliser', label: 'Fertiliser' },
          { key: 'container_price', label: '$/Container' },
          { key: 'container_count', label: 'Num. Containers' },
          { key: 'container_volume', label: 'Container Volume (Kg/Lt)' },
          { key: 'remove', label: 'Remove' },
        ],
      },
      datePicker: datePickerConfig({
        altFormat: 'M j, Y',
        dateFormat: 'Y-m-d',
      }),
      validation: {
        entry_chemical: { required: true },
        entry_fertiliser: { required: true },
        purchased_on: { required: true },
        entry_container_price: { min_value: 0 },
        entry_container_count: { required: true, min_value: 0 },
        entry_counter_volume: { required: true, min_value: 0 },
      },
    };
  },
  computed: {},
  methods: {
    addRow() {
      this.entryRows.push({
        chemical: null,
        container_volume: null,
        container_price: null,
        container_count: null,
      });
    },
    addFertiliserRow() {
      this.fertiliserRows.push({
        fertiliser: null,
        container_volume: null,
        container_price: null,
        container_count: null,
      });
    },
    removeRow(index) {
      this.entryRows.splice(index, 1);
    },
    removeFertiliserRow(index) {
      this.fertiliserRows.splice(index, 1);
    },
    setRow(field, index, value) {
      this.entryRows[index][field] = value;
    },
    setFertiliserRow(field, index, value) {
      this.fertiliserRows[index][field] = value;
    },
    attemptDelete() {
      const notification = this.$notifyConfirm('Deleting a purchase history entry cannot be undone. Are you sure you wish to delete this?', {
        buttons: [
          this.$notification.button('Yes', 'btn btn-primary', () => {
            notification.close();
            this.doDelete();
          }),
          this.$notification.button('Cancel', 'btn btn-light', () => notification.close()),
        ],
      });
    },
    doDelete() {
      this.$apollo
        .mutate({
          mutation: deleteChemicalPurchaseMutation,
          variables: { input: { id: this.id, }, },
        })
        .then(() => {
          this.$notifySuccess(`Purchase entry deleted!`);
          this.$router.back();
        })
        .catch(err => {
          console.error(err);
          this.$notifyFailure('Unfortunately, we were unable to do that!');
        });
    },
    attemptCreate() {
      this.errors.remove('global');
      this.$validator.validateAll().then(validationWasSuccessful => {
        if (!validationWasSuccessful) {
          console.log('validation failed');
          return;
        }

        const mutationInput = {
          purchased_on: this.purchased_on,
          comments: this.comments,
          entries: this.entryRows.map(row => ({
            chemical_id: row.chemical.id,
            container_volume: row.container_volume,
            container_price: row.container_price,
            container_count: row.container_count,
          })),
          fertilisers: this.fertiliserRows.map(row => ({
            fertiliser_id: row.fertiliser.id,
            container_volume: row.container_volume,
            container_price: row.container_price,
            container_count: row.container_count,
          })),
        };

        if(this.id) {
          mutationInput.id = this.id;
        }

        this.$apollo
          .mutate({
            mutation: this.id ? updatePurchaseInputMutation : createPurchaseInputMutation,
            variables: {
              input: mutationInput,
            },
          })
          .then(() => {
            this.$notifySuccess(`Purchase input ${this.id ? 'updated' : 'created'}!`);
            if(!this.id) {
              this.purchased_on = null;
              this.comments = null;
              this.entryRows = Array.from({ length: 3 }, () => ({
                chemical: null,
                container_volume: null,
                container_price: null,
                container_count: null,
              }));
              this.fertiliserRows = Array.from({ length: 3 }, () => ({
                fertiliser: null,
                container_volume: null,
                container_price: null,
                container_count: null,
              }));
            }

            this.$validator.reset();

          })
          .catch(err => this.$mapGraphQLError(err))
          .catch(err => {
            console.error(err);
            this.$notifyFailure('Unfortunately, we were unable to do that!');
          });
      });
    },
  },
};
</script>
